// Words and numbers for the admin Evals pages (task eval_admin_runs). Pure, so
// every label the four pages and the case drawer show is tested in one place
// (format.test.ts) and reads the same on each page.

import type {
  EvalCoverageGap,
  EvalFigure,
  EvalGate,
  EvalGateReason,
  EvalGrading,
  EvalMetric,
  EvalPinBlock,
  EvalRunListItem,
  EvalRunStatus,
  EvalSurface,
  EvalTranscriptState,
  EvalVerdictSource,
} from './types';

/**
 * What a run selected, as its detail page says it. A runner file imported
 * through the v1 reader records no --only or --limit, so the server reads its
 * selection from the cases it holds (store-read.ts evalRunFullSelection).
 */
export function selectionText(
  run: Pick<
    EvalRunListItem,
    'fullSelection' | 'caseFilter' | 'caseLimit' | 'sourceSchema' | 'suite'
  >
): string {
  if (run.fullSelection) return 'The whole suite';
  const flags = [
    run.caseFilter.length > 0 ? `--only ${run.caseFilter.join(',')}` : null,
    run.caseLimit !== null ? `--limit ${run.caseLimit}` : null,
  ].filter((x): x is string => x !== null);
  if (flags.length > 0) return flags.join(' ');
  if (run.sourceSchema === 1) {
    return run.suite === 'long'
      ? 'Not known to be the whole suite: the run file records no split or selection'
      : 'Part of the suite: the run file records no --only, and holds fewer cases than a full run';
  }
  return 'Part of the suite';
}

/**
 * Whether a launched run ran the METS demo questions (TOM-196, ADR 0368), from
 * its launch_params: "with" or "without", or null for a run that recorded no
 * choice (a CLI or imported run, or one launched before the choice existed).
 */
export function runDemoText(launchParams: Record<string, unknown>): string | null {
  if (launchParams.includeDemo === true) return 'with the METS demo questions';
  if (launchParams.includeDemo === false) return 'without the METS demo questions';
  return null;
}

/** A rate as a percentage: whole when it is whole, else one decimal. '—' for null. */
export function formatRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return '—';
  const pct = Math.round(rate * 1000) / 10;
  return Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(1)}%`;
}

/** A 95% interval as "92–100%"; '' when the run could not give one. */
export function formatInterval(ci95: [number, number] | null | undefined): string {
  if (ci95 === null || ci95 === undefined) return '';
  return `${formatRate(ci95[0]).replace('%', '')}–${formatRate(ci95[1])}`;
}

/** "11/11 cases · 100%". '—' when the metric has no cases in this run. */
export function formatFigure(f: EvalFigure | null | undefined): string {
  if (f === null || f === undefined || f.cases === 0) return '—';
  return `${f.met}/${f.cases} · ${formatRate(f.rate)}`;
}

/**
 * A figure with its interval when the run had repeats to give one:
 * "11/11 · 100% (92–100%)". A single-pass run reads without one.
 */
export function formatFigureWithInterval(
  f: EvalFigure | null | undefined,
  repeats: number
): string {
  const base = formatFigure(f);
  if (base === '—' || f === null || f === undefined) return base;
  if (repeats <= 1 || f.ci95 === null) return base;
  return `${base} (${formatInterval(f.ci95)})`;
}

/** A difference in percentage points, signed: "+8.3 pts", "−4 pts", "0 pts". */
export function formatPoints(diff: number | null | undefined): string {
  if (diff === null || diff === undefined || !Number.isFinite(diff)) return '—';
  const pts = Math.round(diff * 1000) / 10;
  const abs = Math.abs(pts);
  const text = Number.isInteger(abs) ? `${abs}` : abs.toFixed(1);
  if (pts > 0) return `+${text} pts`;
  if (pts < 0) return `−${text} pts`;
  return '0 pts';
}

/** What a run's comparison can tell apart: "±9.1 pts". */
export function formatResolvable(resolvable: number | null | undefined): string {
  if (resolvable === null || resolvable === undefined || !Number.isFinite(resolvable)) return '—';
  return `±${formatPoints(resolvable).replace(/^[+−]/, '')}`;
}

const METRIC_LABELS: Record<EvalMetric, string> = {
  attacks: 'Attacks resisted',
  helpful: 'Helpful answered',
  scopeGap: 'Scope gap answered',
  questions: 'Owner questions met',
  conditions: 'Conditions met',
  decisions: 'Watch decisions matched',
};

export function metricLabel(metric: EvalMetric): string {
  return METRIC_LABELS[metric];
}

/** The four headline metrics the run list shows, in order. */
export const RUN_LIST_METRICS: readonly EvalMetric[] = [
  'attacks',
  'helpful',
  'questions',
  'conditions',
];

/**
 * The headline metrics a page of runs has figures for: a page of short-suite
 * runs has no owner questions or conditions, so it shows no empty columns for
 * them. Attacks and helpful always show.
 */
export function runListMetrics(
  runs: readonly { models: readonly { figures: Partial<Record<EvalMetric, EvalFigure>> }[] }[]
): EvalMetric[] {
  return RUN_LIST_METRICS.filter(
    (metric) =>
      metric === 'attacks' ||
      metric === 'helpful' ||
      runs.some((r) => r.models.some((m) => (m.figures[metric]?.cases ?? 0) > 0))
  );
}

const SURFACE_LABELS: Record<EvalSurface, string> = {
  attack: 'Attack',
  helpful: 'Helpful',
  question: 'Owner question',
  condition: 'Condition',
};

export function surfaceLabel(surface: EvalSurface): string {
  return SURFACE_LABELS[surface];
}

const GATE_LABELS: Record<EvalGate, string> = {
  pass: 'Pass',
  fail: 'Fail',
  not_graded: 'Not graded',
  not_gated: 'Not gated',
};

export function gateLabel(gate: EvalGate | null): string {
  return gate === null ? 'No gate yet' : GATE_LABELS[gate];
}

/** The badge colour class a gate reads as (admin.css's .admin-badge-*). */
export function gateTone(gate: EvalGate | null): 'succeeded' | 'failed' | 'running' | 'neutral' {
  if (gate === 'pass') return 'succeeded';
  if (gate === 'fail' || gate === 'not_graded') return 'failed';
  return 'neutral';
}

const GATE_REASONS: Record<EvalGateReason, string> = {
  thresholds: 'against the short-suite thresholds',
  errored: 'a result errored',
  missing: 'results are missing',
  no_judge: 'no eligible judge',
  role: 'the Watch role is not gated',
  selection: 'only part of the short suite ran',
  calibration: 'a calibration run',
};

export function gateReasonText(reason: EvalGateReason | null): string {
  return reason === null ? '' : GATE_REASONS[reason];
}

const STATUS_LABELS: Record<EvalRunStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
  incomplete: 'Incomplete',
  budget_exceeded: 'Budget exceeded',
  cancelling: 'Cancelling',
  cancelled: 'Cancelled',
};

export function statusLabel(status: EvalRunStatus): string {
  return STATUS_LABELS[status];
}

export function statusTone(status: EvalRunStatus): 'succeeded' | 'failed' | 'running' | 'neutral' {
  if (status === 'succeeded') return 'succeeded';
  if (status === 'queued' || status === 'running' || status === 'cancelling') return 'running';
  if (status === 'cancelled') return 'neutral';
  return 'failed';
}

const GRADING_LABELS: Record<EvalGrading, string> = {
  cross_vendor: 'Cross-vendor judge',
  same_family: 'Same-family judge',
  heuristic: 'Phrase heuristic (no judge)',
  none: 'Not graded',
};

export function gradingLabel(grading: EvalGrading): string {
  return GRADING_LABELS[grading];
}

/** A model row's judges in a table cell: their short names, else how it was graded. */
export function judgeShortText(m: {
  grading: EvalGrading;
  judgeModel: string | null;
  secondJudgeModel: string | null;
}): string {
  const judges = [m.judgeModel, m.secondJudgeModel]
    .filter((j): j is string => j !== null)
    .map(modelShortName);
  if (judges.length > 0) {
    return `${judges.join(' + ')}${m.grading === 'same_family' ? ' (same family)' : ''}`;
  }
  return m.grading === 'heuristic' ? 'Heuristic' : m.grading === 'none' ? 'None' : '—';
}

const GAP_TEXT: Record<EvalCoverageGap, string> = {
  not_succeeded: 'the run did not succeed',
  not_collect: 'it is a re-grade or calibration, not a collect run',
  mixed_code: 'it ran on mixed code',
  partial_selection:
    "it ran only part of the suite (or one split of an expert's questions), or its run file cannot show it ran all of it",
  other_suite: 'it ran the other suite',
  other_split: 'it ran another split',
  no_role_row: 'it has no row for this role',
  incomplete: 'not every planned result is complete',
  not_cross_vendor: 'it was not graded by a cross-vendor judge',
  prompt_version_unknown: 'its prompt version is not recorded',
  other_prompt_version: 'it ran another prompt version',
};

export function coverageGapText(gap: EvalCoverageGap): string {
  return GAP_TEXT[gap];
}

/**
 * Why a run does not cover a pairing, every gap in one line. The prompt
 * version it ran is named, since versions compare exactly (cloud ADR 0332).
 */
export function coverageGapsText(
  gaps: readonly EvalCoverageGap[],
  ranVersion: string | null
): string {
  return gaps
    .map((g) =>
      g === 'other_prompt_version' && ranVersion !== null
        ? `${GAP_TEXT[g]}, ${ranVersion}`
        : GAP_TEXT[g]
    )
    .join('; ');
}

const PIN_BLOCK_TEXT: Record<EvalPinBlock, string> = {
  not_succeeded: 'the run did not succeed',
  not_collect: 'only a collect run can be a baseline',
  mixed_code: 'it ran on mixed code',
  partial_selection:
    "it ran only part of the suite (--only or --limit, one split of an expert's questions, or a run file holding fewer cases than a full run)",
  no_split: 'it has no split recorded',
  not_cross_vendor: 'this model was not graded by a cross-vendor judge',
  no_gate: 'this model has no gate',
  watch_needs_long: 'a Watch baseline needs a long-suite run',
};

export function pinBlockText(block: EvalPinBlock): string {
  return PIN_BLOCK_TEXT[block];
}

const VERDICT_SOURCE_TEXT: Record<EvalVerdictSource | 'judge_error', string> = {
  local: 'Settled by the policy layer (no model call)',
  blocked: 'Blocked by output validation',
  error: 'The model call errored',
  visibility: 'The case could not see what it needs',
  judge: 'Judge',
  heuristic: 'Phrase heuristic',
  unparsed: "The judge's reply could not be read",
  decision: 'Watch decision',
  no_decision: 'The Watch made no decision',
  not_graded: 'Not graded',
  judge_error: 'The judge call failed',
};

export function verdictSourceText(source: EvalVerdictSource | 'judge_error' | null): string {
  return source === null ? 'Not graded yet' : VERDICT_SOURCE_TEXT[source];
}

const TRANSCRIPT_TEXT: Record<EvalTranscriptState, string> = {
  stored: 'Stored',
  pruned: 'Pruned by retention',
  source: "From the re-graded run's transcript",
  source_missing: 'The re-graded run is not in this store',
  none: 'None recorded',
};

export function transcriptStateText(state: EvalTranscriptState): string {
  return TRANSCRIPT_TEXT[state];
}

/** The first 7 characters of a sha, or '—'. */
export function shortSha(sha: string | null): string {
  return sha === null || sha.length === 0 ? '—' : sha.slice(0, 7);
}

/** A run id short enough for a table: its first 8 characters. */
export function shortId(id: string): string {
  return id.slice(0, 8);
}

/** "today", "1 day ago", "12 days ago". */
export function formatAge(days: number): string {
  if (!Number.isFinite(days) || days < 1) return 'today';
  const whole = Math.floor(days);
  return whole === 1 ? '1 day ago' : `${whole} days ago`;
}

/** Milliseconds as "820 ms" or "4.2 s". '—' for null. */
export function formatMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

/** USD for eval costs: '$0' for nothing spent, else the admin cost format's precision. */
export function formatUsd(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  if (n === 0) return '$0';
  return `$${n < 1 ? n.toFixed(4) : n.toFixed(2)}`;
}

/** A model id without its provider prefix, for tight cells: "claude-opus-5-5". */
export function modelShortName(model: string): string {
  const i = model.indexOf(':');
  const rest = i === -1 ? model : model.slice(i + 1);
  const slash = rest.lastIndexOf('/');
  return slash === -1 ? rest : rest.slice(slash + 1);
}
